/**
 * A copy-pasteable prompt (for ChatGPT or similar) documenting the
 * report-template token system, so a non-technical admin can generate
 * a custom HTML template externally and paste the result straight into
 * the "HTML source" panel of Settings → Report Templates.
 *
 * Intentional duplication (same convention as REPORT_PREVIEW_TOKENS /
 * REPORT_VARIABLE_GROUPS above and messageFormat.ts's PREVIEW_TOKENS):
 * this is plain prose, not derived from those tables at build time, so
 * that its wording can be curated for an external AI reader. If a
 * token is added to/removed from `buildReportVariableContext` in
 * api/src/modules/work-orders/work-orders.report.js, update the list
 * below (and REPORT_VARIABLE_GROUPS) to match.
 */
export const REPORT_TEMPLATE_AI_SPEC = `SYSTEM / TASK
You are generating a single self-contained HTML document that will be
used as a "Work Order / Task Completion Report" template inside a CMMS
(computerized maintenance management system). Follow every rule below
exactly — this HTML is fed into a real template engine, not read by a
human first.

────────────────────────────────────────────────────────────────────
HOW THE TEMPLATE ENGINE WORKS
────────────────────────────────────────────────────────────────────
- Placeholders look like {{namespace.field}} — e.g. {{wo.title}},
  {{site.field4}}. Only [A-Za-z0-9_.] are allowed inside the braces —
  no spaces, no filters, no pipes.
- This is FLAT STRING SUBSTITUTION ONLY. There is no general-purpose
  {{#if}}, no {{#each}}, no boolean/comparison logic of any kind. Do
  not invent any of that — it will render literally as text, not
  execute. There are exactly THREE deliberate exceptions to this, all
  described in full below — a row-hiding attribute, a checklist-answer
  row-repeating block, and a per-checklist section-repeating block —
  nothing else like any of them exists, so don't assume any other
  conditional or loop syntax works.
- Every placeholder below is either:
    (a) a SCALAR — replaced with a plain (HTML-escaped) text value, or
    (b) a BLOCK — replaced with a pre-built, ready-to-use HTML chunk
        (its own heading, borders, spacing already included).
  Blocks must be placed ALONE on their own line/row — do not wrap them
  in your own extra heading or container, and do not put other text
  next to them on the same line.
- If a work order has no data for something (e.g. no parts were used,
  no checklist attached), that block's placeholder is simply replaced
  with an empty string — so leave it in the layout; it will just
  disappear when not applicable.
- Any {{token}} not in the list below silently renders as nothing
  (blank) in production. Only use tokens from this exact list.
- Scalars are different: an empty scalar leaves the {{token}} spot
  blank, but any static label text you wrote next to it (e.g. a table
  cell that says "Category") still renders — producing a label sitting
  next to an empty cell. For any <tr> that pairs a label with ONE
  scalar value (a "Details" grid row like Site / Location / Asset /
  Category), add the row-hiding attribute so the whole row disappears
  instead of showing a blank value next to its label:
    <tr data-hz-hide-if-empty="location.name">
      <td>Location</td><td>{{location.name}}</td>
    </tr>
  When {{location.name}} is blank for that work order, the entire
  <tr> — label and all — is dropped from the output. Use the exact
  same token name in the attribute as the one you placed in the row.
  This ONLY works on a <tr> (not a <div> or any other element). Apply
  this to every label/value Details-grid row you author — do NOT apply
  it to rows whose value always has a fallback (e.g. a date field,
  which renders "—" instead of going blank) since those never trigger
  it.
- A work order/task can have MORE THAN ONE checklist executed against
  it. Three ways to show that, pick ONE per template:
    1. {{checklist_table}} alone — simplest. Already renders one
       heading+table per checklist, one after another, no setup needed.
    2. {{#checklist_item}} (see below) — for your own row design in ONE
       merged table across every checklist. {{item.section}} is
       prefixed with the checklist's own name (e.g.
       "Fire Safety Audit — General") whenever there's more than one,
       so answers stay distinguishable in a single flat table.
    3. {{#checklist}} ... {{/checklist}} — for your own heading design
       PER checklist (a fully separate section for each, not one merged
       table). Everything between the two markers repeats once per
       executed checklist. INSIDE this loop, {{checklist.name}},
       {{checklist.score}}, {{checklist.completion_time}},
       {{checklist_table}}, and a nested {{#checklist_item}} all scope
       to THAT ONE checklist only (its own name/score/time, its own
       items — not prefixed, since there's no ambiguity inside one
       iteration). Example:
         {{#checklist}}
         <div>{{checklist.name}} — Score: {{checklist.score}}</div>
         <table><tbody>
           {{#checklist_item}}
           <tr><td>{{item.label}}</td><td>{{item.value}}</td></tr>
           {{/checklist_item}}
         </tbody></table>
         {{/checklist}}
       Do not nest {{#checklist}} inside {{#checklist_item}} or vice
       versa in the wrong order — {{#checklist_item}} must be INSIDE
       {{#checklist}}, never the reverse.
  Outside of a {{#checklist}} loop, the three singular scalars
  {{checklist.name}}, {{checklist.score}}, {{checklist.completion_time}}
  can only ever show ONE checklist's values (the first/most recently
  started one) — a flat text token can't hold N different checklists'
  names/scores at once. Don't build a header around those three scalars
  OUTSIDE a {{#checklist}} loop if this org's work orders commonly run
  more than one checklist — use option 3 above instead if you want a
  real per-checklist header.
- checklist_table (below) is a fixed, pre-built layout — if you want
  your OWN row design for the checklist instead (your own columns,
  styling, which fields to show), use {{#checklist_item}} ...
  {{/checklist_item}} instead of the {{checklist_table}} block.
  Everything between those two markers is YOUR row template, repeated
  once for every real checklist answer on this work order, across
  EVERY executed checklist. Inside it, use: {{item.label}} (the
  question text), {{item.value}} (the answer), {{item.note}} (already
  reads "Note: <text>" when the technician left one, or a blank string
  if not — place it bare, do NOT write your own "Note: {{item.note}}",
  that would double the label), {{item.flag_label}} ("Flagged" or
  blank, same pre-labeled pattern), {{item.section}} (which checklist
  section this item belongs to — prefixed with the checklist's own
  name, e.g. "Fire Safety Audit — General", whenever this work order
  has more than one checklist attached, so answers from different
  checklists can still be told apart in a flat table), {{item.media}}
  (pre-rendered photo/audio HTML for that answer, if any — leave this
  as a bare placeholder, don't try to rebuild it). Example:
    <table><tbody>
      {{#checklist_item}}
      <tr><td>{{item.section}}</td><td>{{item.label}}</td><td>{{item.value}}</td></tr>
      {{/checklist_item}}
    </tbody></table>
  Showing {{item.section}} on every row (as above) is the only way a
  flat table communicates which checklist/section an answer belongs
  to — there's no logic available to detect "this row starts a new
  checklist or section" and print a header only then. Do not invent
  similar loop syntax for parts, comments, attachments, or signatures
  — only checklist_item exists; those stay fixed blocks (parts_table,
  comments_list, attachments_list, signatures_block).

────────────────────────────────────────────────────────────────────
HARD OUTPUT CONSTRAINTS (this HTML is also emailed directly, not just
turned into a PDF — it must survive Outlook/Gmail's HTML rendering,
which is much stricter than a normal browser)
────────────────────────────────────────────────────────────────────
1. Output ONE complete HTML document: <!DOCTYPE html>, <html>, <head>,
   <body>. No markdown, no explanation, no code fences — just the raw
   HTML.
2. ALL styling must be inline style="..." attributes on each element.
   A <style> block in <head> is fine for @page/@media print only —
   never rely on it for layout, many email clients strip it.
3. Build the layout with nested HTML <table>/<tr>/<td> elements, NOT
   CSS flexbox or CSS grid. This is the standard "email-safe HTML"
   convention — flexbox/grid silently fail in Outlook.
4. No <script> tags, no JavaScript, no external stylesheets, no web
   fonts. Font stack: -apple-system, BlinkMacSystemFont, 'Segoe UI',
   Roboto, sans-serif (or a similar safe system stack).
5. The PDF page is A4, ~794px wide (96dpi) at zero margin. Content
   wider than that isn't clipped — the PDF export shrinks the whole
   render down to fit the page, same as a browser's print preview
   "fit to page width" default. So going slightly over budget degrades
   gracefully in the PDF (a bit smaller than you designed), not
   catastrophically. BUT: the editor's live preview does NOT do this
   shrinking — it renders at a fixed 794px and will show oversized
   content overflowing/scrolling instead, so it stops matching the
   real PDF once you're over budget. Treat 794px as a real budget for
   the TOTAL rendered width of your outermost element (padding
   included) if you want the preview to be trustworthy — e.g. a
   <td align="center" style="padding:24px 16px"> wrapping a 794px-wide
   inner table is already 794+16+16=826px over budget. Two safe
   patterns:
     a) Full-bleed: outermost table width:100% with no fixed pixel
        width anywhere, so it naturally fills whatever container it's
        given — no budget math needed.
     b) Card-on-backdrop: pick an inner card width, then verify
        card_width + (left padding of every ancestor) +
        (right padding of every ancestor) <= 794. A 720px card inside
        a <td style="padding:24px 16px"> is 720+32=752 — safe. A
        740px+ card with that same padding is not.
   When in doubt, use (a) or keep the card at 700px or narrower.
6. No position:fixed/absolute, no JS-driven interactivity.
7. Keep it to one visual "theme" — e.g. one accent color used
   consistently for the header/badges — don't mix multiple unrelated
   color schemes.
8. The PDF export is a real browser print, so it can paginate onto
   multiple pages exactly like printing a long webpage — add
   style="page-break-inside:avoid;break-inside:avoid" to every <tr>,
   and every self-contained <div> card (a table row, an image with its
   caption, a signature block, a comment), so a page break can never
   land in the middle of one. Only skip this on large section wrappers
   that are expected to span multiple pages (e.g. a wrapper containing
   an entire long table) — apply it at the row/card level, not the
   section level.
9. External images (a logo hotlinked from your own marketing site, an
   icon from a public CDN) are fine — the PDF export loads them the
   normal way a browser does. Keep templates self-contained where you
   can, but an <img src="https://..."> pointed at a real, reachable
   image is a safe, supported pattern here.

────────────────────────────────────────────────────────────────────
AVAILABLE VARIABLES
────────────────────────────────────────────────────────────────────

SCALARS — work order / task
  {{wo.code}}          e.g. "WO-00123"
  {{wo.title}}          e.g. "Replace HVAC filter on Unit A-101"
  {{wo.description}}    free text, may contain <br/> already applied for line breaks
  {{wo.status}}         "Open" | "In Progress" | "Closed" | "Resolved"
  {{wo.priority}}       "Critical" | "High" | "Medium" | "Low"
  {{wo.due_date}}       pre-formatted, e.g. "Sep 12, 2026, 05:00 PM"
  {{wo.work_type}}      e.g. "Preventive" | "Reactive"
  {{wo.category}}       e.g. "HVAC"
  {{wo.kind_label}}     "Work Order" or "Task"

SCALARS — site
  {{site.name}}
  {{site.address}}
  {{site.entity}}
  {{site.state}}
  {{site.category}}
  {{site.field1}} … {{site.field5}}   org-defined custom fields (label varies per org — just call them by position, e.g. "Custom field")
  {{site.branch_area_sqft}}

SCALARS — location / asset
  {{location.name}}
  {{asset.name}}
  {{asset.code}}

SCALARS — people
  {{assigned.name}}
  {{reporter.name}}
  {{created_by.name}}

SCALARS — checklist
  {{checklist.name}}              only the FIRST checklist executed on this work order — see note above
  {{checklist.score}}             e.g. "92%" (blank if that checklist isn't scored)
  {{checklist.completion_time}}   e.g. "18m"

SCALARS — dates
  {{dates.created_at}}
  {{dates.closed_at}}
  {{dates.resolved_at}}
  {{dates.total_duration}}        e.g. "2h 25m"

SCALARS — organization
  {{org.currency_symbol}}         e.g. "$"

BLOCKS (pre-rendered HTML, place as a standalone token — each already
includes its own section heading, borders, and spacing; each collapses
to nothing when not applicable)
  {{timeline_block}}       a vertical timeline of status-change events
  {{checklist_table}}      every filled-out inspection checklist on this work order (Q&A table, photos, flags — one section per checklist if there's more than one)
  {{parts_table}}          parts/materials used, with cost total
  {{signatures_block}}     captured close/acknowledge signature images
  {{attachments_list}}     work-order-level file attachments (photos etc.)
  {{comments_list}}        the comment thread

CHECKLIST ROW LOOP (use INSTEAD of {{checklist_table}} if you want your
own row design — see the full explanation above for rules/limits;
includes every executed checklist on this work order, not just one)
  {{#checklist_item}} ... {{/checklist_item}}
    {{item.label}}        the question text
    {{item.value}}        the answer
    {{item.note}}         "Note: <text>", pre-labeled, blank if none
    {{item.flag_label}}   "Flagged" or blank
    {{item.section}}      checklist section this item is in — prefixed
                           with the checklist's own name when this work
                           order has more than one checklist attached
    {{item.media}}        pre-rendered photo/audio HTML, blank if none

PER-CHECKLIST SECTION LOOP (use INSTEAD of {{checklist_table}}/
{{#checklist_item}} if you want your own heading design PER checklist,
not one merged table — see option 3 in the full explanation above)
  {{#checklist}} ... {{/checklist}}
    Repeats everything inside it once per executed checklist. Inside
    this loop only, these scope to THAT ONE checklist (not merged):
    {{checklist.name}}
    {{checklist.score}}
    {{checklist.completion_time}}
    {{checklist_table}}         just this one checklist's table
    {{#checklist_item}} ... {{/checklist_item}}   just this one checklist's items (item.section is plain, not prefixed, inside here)

────────────────────────────────────────────────────────────────────
WHAT TO BUILD
────────────────────────────────────────────────────────────────────
[Describe here what you want it to look like — e.g. "a clean corporate
report with a navy header, our logo placeholder at the top, and a
two-column summary card row" — then ask for the finished HTML.]`
